import { lazy } from 'react'

const RELOAD_FLAG = 'retira:chunk-reload'

// Stale chunk recovery for dynamic imports after a deploy.
//
// When the app is open and a new version is deployed, the hashed chunk files
// referenced by the already-loaded index.html are replaced, so a later dynamic
// import (a lazy page, or an on-demand chunk like the PDF generator) fails with
// a 404 / "failed to fetch dynamically imported module".
//
// On the first such failure we reload the page once to pick up the fresh
// index.html + chunk hashes. A sessionStorage flag prevents an infinite reload
// loop if the chunk is genuinely broken rather than just stale; in that case the
// error is rethrown so the caller (or an error boundary) can handle it.
//
// `importWithReload` returns a promise that, on a stale-chunk failure, never
// resolves (the page is reloading), so callers naturally pause until the reload
// navigates away.
export function importWithReload(factory) {
  return factory()
    .then((module) => {
      // Successful load – clear the guard so a future deploy can recover too.
      try { window.sessionStorage?.removeItem(RELOAD_FLAG) } catch { /* ignore */ }
      return module
    })
    .catch((error) => {
      let alreadyReloaded = false
      try {
        alreadyReloaded = window.sessionStorage?.getItem(RELOAD_FLAG) === '1'
        if (!alreadyReloaded) window.sessionStorage?.setItem(RELOAD_FLAG, '1')
      } catch { /* sessionStorage unavailable – fall through to rethrow */ }

      if (!alreadyReloaded) {
        window.location.reload()
        // Never resolve: hold the caller until the reload navigates away.
        return new Promise(() => {})
      }

      throw error
    })
}

// React.lazy hardened against stale chunk hashes after a deploy. Without this,
// a stale lazy import unmounts the tree and leaves a white screen until a manual
// refresh; here the page reloads once to recover. See importWithReload above.
export function lazyWithReload(factory) {
  return lazy(() => importWithReload(factory))
}
